import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Chip,
  Box,
  Collapse,
  IconButton,
  Divider,
  InputAdornment,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// fetch
import { fetchProfile } from "../../utils/fetchProfile";
import patchData from "./Docs/notes";

// component
import Navbar from "../../Components/Navbar";
import Footer from "../../Components/Footer";

export default function Patch() {
  const accessToken = Cookies.get("access_token") || "";

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [dataProfile, setDataProfile] = useState([]);
  const [notAllowAccessPage, setNotAllowAccessPage] = useState(false);
  const [openYears, setOpenYears] = useState({});

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProfile(accessToken, setDataProfile, setNotAllowAccessPage, []);
    }, 200);
    return () => clearTimeout(handler);
  }, [accessToken]);

  const groupedByYear = patchData.reduce((acc, patch) => {
    if (!acc[patch.year]) acc[patch.year] = [];
    acc[patch.year].push(patch);
    return acc;
  }, {});

  useEffect(() => {
    const initialState = {};
    Object.keys(groupedByYear).forEach((year) => {
      initialState[year] = year === String(currentYear);
    });
    setOpenYears(initialState);
  }, []);

  const toggleYear = (year) => {
    setOpenYears((prev) => ({ ...prev, [year]: !prev[year] }));
  };

  if (notAllowAccessPage) {
    return (
      <div>
        <Navbar
          active="Patch Notes"
          initial={dataProfile.initial}
          role_code={dataProfile.role_code}
        />
        <div>Not allowed to access this page</div>
      </div>
    );
  }

  return (
    <div>
      <Navbar
        active="Patch Notes"
        initial={dataProfile.initial}
        role_code={dataProfile.role_code}
      />
      <Box
        sx={{
          width: { xs: "92%", sm: "90%", md: "70%", lg: "60%" },
          mx: "auto",
          mt: { xs: 4, sm: 6, md: 8 },
          px: { xs: "4%", sm: 3, md: 4 },
          mb: 20,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 32, sm: 40, md: 48 },
            fontWeight: 800,
            fontFamily: "Montserrat",
            textAlign: "center",
            mt: { xs: 6, sm: 8, md: 12 },
            mb: { xs: 4, sm: 6, md: 12 },
          }}
        >
          PATCH NOTES
        </Typography>

        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            placeholder="Search patch notes..."
            value={searchQuery}
            onChange={(e) => {
              const query = e.target.value.toLowerCase();
              setSearchQuery(query);
          
              const results = patchData
                .filter((patch) =>
                  patch.title.toLowerCase().includes(query) ||
                  patch.description.toLowerCase().includes(query) ||
                  patch.changelog?.some((c) => c.toLowerCase().includes(query)) ||
                  patch.datas?.some((data) =>
                    data.title.toLowerCase().includes(query) ||
                    data.items?.some((item) => item.toLowerCase().includes(query))
                  )
                )
                .slice(0, 5);
          
              setSearchResults(results);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery("");
                      setSearchResults([]);
                    }}
                  >
                    ✕
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {searchQuery && (
            <Paper elevation={3} sx={{ mt: 1 }}>
              <List>
                {searchResults.length > 0 ? (
                  searchResults.map((result) => (
                    <ListItem
                      button
                      key={result.id}
                      onClick={() =>
                        window.open(`/patch-notes/${result.id}`, "_blank")
                      }
                    >
                      <ListItemText
                        primary={result.title}
                        secondary={result.description}
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No results found." />
                  </ListItem>
                )}
              </List>
            </Paper>
          )}
        </Box>

        {/* Patch Notes Display */}
        {Object.keys(groupedByYear)
          .sort((a, b) => b - a)
          .map((year) => (
            <Box key={year} sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  mb: 2,
                }}
                onClick={() => toggleYear(year)}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 20, md: 24 } }}
                >
                  {year} - Updates
                </Typography>
                <IconButton size="small">
                  {openYears[year] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={openYears[year]} timeout="auto" unmountOnExit>
                <Grid container spacing={2}>
                  {groupedByYear[year].map((patch) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={patch.id}>
                      <Card
                        onClick={() =>
                          window.open(`/patch-notes/${patch.id}`, "_blank")
                        }
                        sx={{
                          cursor: "pointer",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          border: "1px solid #f8f8f8",
                          transition: "box-shadow 0.3s",
                          "&:hover": {
                            boxShadow: 3,
                          },
                        }}
                      >
                        <CardHeader
                          title={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              {(Array.isArray(patch.type)
                                ? patch.type
                                : [patch.type]
                              ).map((t, i) => (
                                <Chip
                                  key={i}
                                  label={t}
                                  size="small"
                                  sx={{
                                    bgcolor: `${
                                      (Array.isArray(patch.color)
                                        ? patch.color
                                        : [patch.color])[i]
                                    }33`,
                                    color: (Array.isArray(patch.color)
                                      ? patch.color
                                      : [patch.color])[i],
                                    fontWeight: "bold",
                                  }}
                                />
                              ))}
                            </Box>
                          }
                          sx={{ pb: 0 }}
                        />
                        <CardContent>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              fontFamily: "Montserrat",
                              fontSize: { xs: 16, sm: 18 },
                              mb: 1,
                            }}
                          >
                            {patch.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: 12, sm: 14 } }}
                          >
                            {patch.description.length > 50
                              ? `${patch.description.slice(0, 50)}...`
                              : patch.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
              <Divider sx={{ mt: 4 }} />
            </Box>
          ))}
      </Box>
      <Footer />
    </div>
  );
}
